import React, { useContext, useEffect, useState } from 'react'
import { Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { ReservationContext } from '../../context/ReservationContext'
import NumberField from '../../utils/NumberField'
import PropTypes from 'prop-types'

export function Categories({ setPreviousStepCompleted }) {
  const { categories, setCategories, totalTickets, totalPrice } = useContext(ReservationContext)

  const [categoryQuantities, setCategoryQuantities] = useState({})

  // Helper function to get the actual quantity in state to display
  const getCategoryQuantity = (id) => {
    return categoryQuantities[id] !== undefined ? categoryQuantities[id] : categories.find((c) => c.id === id)?.quantity || 0
  }

  const handleQuantityChange = (id, newQuantity) => {
    // Validate that user can't go over limit of 30 tickets
    if (newQuantity < 0) return // Prevent negative values

    // Create a temporary array to test what would happen with change
    const updatedCategories = categories.map((category) => (category.id === id ? { ...category, quantity: newQuantity } : category))

    // Calculate the potential total tickets with this change
    let newTotal = 0
    updatedCategories.forEach((category) => {
      newTotal += category.quantity
    })

    if (newTotal <= 30) {
      setCategories((prev) => prev.map((category) => (category.id === id ? { ...category, quantity: newQuantity } : category)))
      // Also update the local state to make sure UI updates properly
      setCategoryQuantities((prev) => ({ ...prev, [id]: newQuantity }))
    }
  }

  useEffect(() => {
    // Update our local category quantities when categories change
    const newQuantities = {}
    categories.forEach((category) => {
      newQuantities[category.id] = category.quantity
    })
    setCategoryQuantities(newQuantities)

    setPreviousStepCompleted(totalTickets > 0)
  }, [categories, totalTickets, setPreviousStepCompleted])

  return (
    <Paper
      elevation={3}
      sx={{
        minHeight: '400px',
        maxHeight: { xs: '50vh', sm: '60vh' },
        overflowY: 'auto',
        p: { xs: 1, sm: 2 },
        borderRadius: 10
      }}
    >
      <Typography variant='h5' sx={{ mb: 1, fontWeight: 'bold', color: 'title.main' }}>
        2. Choix des catégories
      </Typography>

      <Box
        sx={{
          maxWidth: 700,
          mx: 'auto',
          flex: 1,
          p: { xs: 0, sm: 1 },
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Typography variant='h6' gutterBottom>
          Sélectionnez vos billets (max. 30)
        </Typography>

        {categories?.length > 0 ? (
          <TableContainer sx={{ mb: { xs: 0, sm: 1 } }}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    mb: 0,
                    '& .MuiTableCell-root': {
                      fontWeight: 'bold',
                      color: 'title.main',
                      textAlign: 'center'
                    }
                  }}
                >
                  <TableCell>Catégorie</TableCell>
                  <TableCell>Prix unitaire</TableCell>
                  <TableCell>Quantité</TableCell>
                  <TableCell>Sous-total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category) => {
                  if (category.active)
                    return (
                      <>
                        <TableRow key={category.id}>
                          <TableCell>
                            <Stack>
                              {category.name}
                              {category.name === 'Passeport ABCD' && (
                                <Typography variant='caption' display='block' gutterBottom>
                                  (Sur présentation du passeport ABCD à l&apos;entrée)
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>{category.price} €</TableCell>
                          <TableCell>
                            <NumberField
                              min={0}
                              max={30}
                              value={getCategoryQuantity(category.id)}
                              onValueChange={(value) => handleQuantityChange(category.id, value ?? 0)}
                              sx={{
                                mx: 1,
                                width: { xs: '65px', sm: '80px' }
                              }}
                            />
                          </TableCell>

                          <TableCell>{category.price * category.quantity} €</TableCell>
                        </TableRow>
                      </>
                    )
                })}
                <TableRow
                  key='total'
                  sx={{
                    mb: 0,
                    '& .MuiTableCell-root': {
                      fontWeight: 'bold',
                      color: 'title.main',
                      textAlign: 'center'
                    }
                  }}
                >
                  <TableCell>
                    <Typography variant='h6'>Total</Typography>
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell>
                    <Typography variant='h6'>
                      {totalTickets} billet{totalTickets > 1 ? 's' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='h6'>Total: {totalPrice / 100} €</Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          'Aucune place en vente pour le moment'
        )}
      </Box>
    </Paper>
  )
}

Categories.propTypes = {
  setPreviousStepCompleted: PropTypes.func.isRequired
}
